export interface CategoryColor {
	slug: string
	label: string
	hex: string
}

export const CATEGORY_COLORS: CategoryColor[] = [
	{ slug: 'rouge', label: 'Rouge', hex: '#dc2626' },
	{ slug: 'orange', label: 'Orange', hex: '#ea580c' },
	{ slug: 'ambre', label: 'Ambre', hex: '#d97706' },
	{ slug: 'or', label: 'Or', hex: '#ca8a04' },
	{ slug: 'vert', label: 'Vert', hex: '#16a34a' },
	{ slug: 'emeraude', label: 'Émeraude', hex: '#059669' },
	{ slug: 'cyan', label: 'Cyan', hex: '#0891b2' },
	{ slug: 'bleu', label: 'Bleu', hex: '#2563eb' },
	{ slug: 'indigo', label: 'Indigo', hex: '#4f46e5' },
	{ slug: 'violet', label: 'Violet', hex: '#7c3aed' },
	{ slug: 'rose', label: 'Rose', hex: '#db2777' },
	{ slug: 'ardoise', label: 'Ardoise', hex: '#475569' },
]

const COLOR_MAP = new Map(CATEGORY_COLORS.map((c) => [c.slug, c.hex]))

export function getCategoryColorHex(slug: string): string {
	return COLOR_MAP.get(slug) ?? '#475569'
}

/**
 * Returns a light tint (~L 92%) of the given hex colour for use as a
 * background. Computed via hex → HSL → set L to 92% → hex. No external
 * colour library required.
 *
 * Examples:
 *   tintFromHex('#dc2626') → a very light red
 *   tintFromHex('#2563eb') → a very light blue
 */
export function tintFromHex(hex: string): string {
	// Strip leading # and parse R, G, B
	const clean = hex.replace(/^#/, '')
	const r = parseInt(clean.slice(0, 2), 16) / 255
	const g = parseInt(clean.slice(2, 4), 16) / 255
	const b = parseInt(clean.slice(4, 6), 16) / 255

	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	const delta = max - min

	// Lightness
	let l = (max + min) / 2

	// Saturation
	let s = 0
	if (delta !== 0) {
		s = delta / (1 - Math.abs(2 * l - 1))
	}

	// Hue
	let h = 0
	if (delta !== 0) {
		if (max === r) {
			h = ((g - b) / delta) % 6
		} else if (max === g) {
			h = (b - r) / delta + 2
		} else {
			h = (r - g) / delta + 4
		}
		h = Math.round(h * 60)
		if (h < 0) h += 360
	}

	// Set lightness to 92% for the tint
	l = 0.92
	// Keep saturation reasonable — very low sat looks grey; boost it slightly
	s = Math.max(s, 0.3)

	// HSL → RGB
	const c = (1 - Math.abs(2 * l - 1)) * s
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
	const m = l - c / 2

	let r1 = 0,
		g1 = 0,
		b1 = 0
	if (h < 60) {
		;[r1, g1, b1] = [c, x, 0]
	} else if (h < 120) {
		;[r1, g1, b1] = [x, c, 0]
	} else if (h < 180) {
		;[r1, g1, b1] = [0, c, x]
	} else if (h < 240) {
		;[r1, g1, b1] = [0, x, c]
	} else if (h < 300) {
		;[r1, g1, b1] = [x, 0, c]
	} else {
		;[r1, g1, b1] = [c, 0, x]
	}

	const toHex = (n: number) =>
		Math.round((n + m) * 255)
			.toString(16)
			.padStart(2, '0')

	return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`
}
